'use client';

import { forwardRef, type InputHTMLAttributes } from 'react';
import styles from './input.module.css';

/**
 * Input - the design-system text input. One canonical control so every form
 * row across the app reads from the same vocabulary as Button and Select: the
 * two sizes share their metrics with the Select trigger (`sm` sits flush next
 * to a `size="sm"` Select in the same row), the focus ring is the shared
 * accent ring, and every colour is a token.
 *
 *   - `size`   'md' (default) | 'sm' - matches ButtonSize / Select size.
 *   - `quiet`  inline-edit look: no chrome at rest, hairline on hover, the
 *     full input on focus (a title or label that happens to be editable).
 *   - `mono`   the stable-id look for refs and keys (`person:ari`).
 *   - `invalid` recolours the border to the signal tone and sets
 *     `aria-invalid`; pair it with a visible message, the border alone
 *     never carries the meaning.
 *
 * Renders a real `<input>`, forwards its ref, passes through every native
 * prop (`type="date"` / `"number"` work unchanged). The native `size`
 * attribute is intentionally shadowed - column widths belong to the layout,
 * not the control.
 */

export type InputSize = 'sm' | 'md';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize;
  /** Inline-edit look: chromeless at rest, input chrome on hover/focus. */
  quiet?: boolean;
  /** Monospace, for stable ids and refs (never prose). */
  mono?: boolean;
  /** Signal an invalid value (border + aria-invalid); add a message nearby. */
  invalid?: boolean;
  /** No chrome: no border, no fill, no padding - the text sits in its host's
   *  geometry (a checklist's entry line, an editable heading). The host draws
   *  the affordance. */
  bare?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', quiet, mono, invalid, bare, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={className ? `${styles.input} ${className}` : styles.input}
      data-size={size}
      data-bare={bare || undefined}
      data-quiet={quiet || undefined}
      data-mono={mono || undefined}
      data-invalid={invalid || undefined}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
});
